/**
 * Confirmation Service - Handles final confirmation step logic
 * Single responsibility: Manage confirmation step state and navigation
 */

import type { Dispatch } from '@reduxjs/toolkit';

import type { CopilotState } from '../data-layer/types';
import {
  addMessage,
  hideAttributeMapping,
  setActiveStep,
  updateNameToLabelMap,
} from '../data-store/copilotSlice';

import { addUserResponse } from './messageService';

interface ConfirmationServiceConfig {
  dispatch: Dispatch;
  getState: () => unknown;
}

export default class ConfirmationService {
  private dispatch: Dispatch;

  private getState: () => unknown;

  constructor(config: ConfirmationServiceConfig) {
    this.dispatch = config.dispatch;
    this.getState = config.getState;
  }

  /**
   * Show confirmation step with proper navigation setup
   */
  showConfirmationStep(): void {
    // Check if confirmation message already exists (only prevent in create mode)
    const state = this.getState() as {
      copilot?: {
        messages?: { subType?: string }[];
        navigation?: { skippedSteps?: string[] };
        ui?: { mode?: string };
      };
    };

    const messages = state.copilot?.messages ?? [];
    const isEditMode = state.copilot?.ui?.mode === 'edit';
    const confirmationExists = messages.some(
      (msg) => msg.subType === 'confirmOnboard'
    );

    // In create mode, prevent duplicates; in edit mode, allow normal flow
    if (!isEditMode && confirmationExists) {
      return;
    }

    // Set up navigation for Confirmation step
    this.dispatch(
      updateNameToLabelMap({ stepName: 'Confirmation', label: 'Confirmation' })
    );

    // Set Confirmation as active step
    this.dispatch(setActiveStep('Confirmation'));

    // Hide attribute mapping
    this.dispatch(hideAttributeMapping());

    // Get skipped steps for conditional button logic
    const skippedSteps = state.copilot?.navigation?.skippedSteps ?? [];

    // Build buttons array
    const buttons = [
      {
        label: 'Yes,Go Ahead',
        action: 'handleNavigationBackToListing:Yes,Go Ahead',
      },
    ];

    // Add "I want to Add More" button if there are skipped steps
    if (skippedSteps.length > 0) {
      buttons.push({ label: 'I want to Add More', action: 'handleFinalCall' });
    }

    // Show confirmation message
    this.dispatch(
      addMessage({
        id: `final-confirmation-${Date.now()}`,
        type: 'ai_response',
        subType: 'confirmOnboard',
        timestamp: Date.now(),
        buttons,
      })
    );
  }

  /**
   * Check if there are skipped steps
   */
  hasSkippedSteps(): boolean {
    const state = this.getState() as {
      copilot?: CopilotState;
    };
    const skippedSteps = state.copilot?.navigation.skippedSteps ?? [];

    return skippedSteps.length > 0;
  }

  /**
   * Get list of skipped steps
   */
  getSkippedSteps(): string[] {
    const state = this.getState() as {
      copilot?: CopilotState;
    };

    return state.copilot?.navigation.skippedSteps ?? [];
  }

  /**
   * Handle "I want to add more" button click
   */
  handleFinalCall(): void {
    // Add user response
    addUserResponse(this.dispatch, 'I want to Add More', 'userResponseYes');

    // Get skipped steps
    const skippedSteps = this.getSkippedSteps();
    const extendOption: string[] = [...skippedSteps, "I'll set this up later"];

    // Show recommendations message with skipped steps
    this.dispatch(
      addMessage({
        id: 'final-recommendations-message',
        type: 'ai_response',
        subType: 'finalRecommendations',
        timestamp: Date.now(),
        values: extendOption.length > 0 ? [extendOption.join(', ')] : ['None'],
      })
    );
  }
}
